import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createEditor, DecoratedRange, Editor, Node } from 'slate';
import { Editable, ReactEditor, Slate, withReact } from 'slate-react';

import { TTranscription } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentDetailCard/components/BroadcastScheduleProgramBodySegmentDetailCardBodySegmentTranscription';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { useSegmentTranscriptionsPermissions } from '@/features/broadcast-schedule-program/hooks/useSegmentTranscriptionsPermissions';
import { useClipTranscriptionsPermissions } from '@/features/clips/hooks/useClipTranscriptionsPermissions';
import { TASRRecognitionResult } from '@/features/segments/types';
import { useUnderMinDesktopWidth } from '@/hooks/useUnderMinDesktopWidth';
import { HighlightStyles } from '@/utils/hightlights';

import { getSecondsFromStart, transcriptionToSlateValue } from './slate-utils';

type TranscriptionUpdate = {
  asrRecognitionResults: TASRRecognitionResult[];
};

export type TranscriptionProps = {
  transcriptions: TTranscription;
  seconds?: number;
  textToHighlight?: string;
  searchTextToHighlight?: string;
  setSelectedMention?: (mentionId: string) => void;
  onSelection?: (selection: string) => void;
  itemStartDatetime?: string;
  onUpdate: (transcriptionId: string, data: TranscriptionUpdate) => void;
};

type ExtendedDecoratedRange = DecoratedRange & {
  highlight?: boolean;
  mentionHighlight?: boolean;
  mentionSecond?: number;
  manualHighlight?: boolean;
  highlightOrange?: boolean;
};

export const Transcription = ({
  transcriptions,
  seconds = 0,
  textToHighlight,
  searchTextToHighlight,
  setSelectedMention,
  onSelection,
  itemStartDatetime,
  onUpdate,
}: TranscriptionProps) => {
  const { videoControlRef, setSelectedMentionTime } = useBroadcastScheduleProgram();
  const editor = useMemo(() => withReact(createEditor() as ReactEditor), []);
  const slateValue = transcriptionToSlateValue(transcriptions);
  const { underMinDesktopWith } = useUnderMinDesktopWidth();

  const [editingTextId, setEditingTextId] = useState<number | null>(null);
  const [editingSpeakerLabelId, setEditingSpeakerLabelId] = useState<number | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);

  const { canUpdate: canUpdateTranscriptions } = useSegmentTranscriptionsPermissions();
  const { canUpdate: canUpdateClipTranscriptions } = useClipTranscriptionsPermissions();

  const getSelection = (editor: ReactEditor): string | null => {
    const domSelection = window.getSelection();
    if (domSelection && !domSelection.isCollapsed) {
      const text = domSelection.toString().trim();
      if (text && text.length > 0) {
        return text;
      }
    }

    const selection = editor.selection;
    if (!selection) {
      return null;
    }

    try {
      const text = Editor.string(editor, selection).trim();
      return text.length > 0 ? text : null;
    } catch (e) {
      console.error('Error getting Slate selection:', e);
      return null;
    }
  };

  const handleMouseDown = useCallback(() => {
    setIsSelecting(true);
  }, []);

  const handleMouseUp = useCallback(() => {
    if (isSelecting) {
      setTimeout(() => {
        const selection = getSelection(editor);
        if (selection && selection.length > 0) {
          setSelectedMention && setSelectedMention(selection);

          const matchedMention = slateValue
            .flatMap(child => child.children)
            ?.find(mention => mention.text.toLowerCase().includes(selection.toLowerCase()));

          const seconds = matchedMention?.startOffset;

          const newDate = new Date(
            new Date(itemStartDatetime || '').getTime() + (seconds || 0) * 1000
          );

          setSelectedMentionTime?.(newDate);

          onSelection && onSelection(selection);
        }
      }, 10);
    }
    setIsSelecting(false);
  }, [
    isSelecting,
    editor,
    setSelectedMention,
    onSelection,
    transcriptions.mentions,
    itemStartDatetime,
    setSelectedMentionTime,
  ]);

  const onDoubleClickHandler = useCallback(
    (item: number, type: 'text' | 'speaker') => {
      if (!canUpdateTranscriptions || !canUpdateClipTranscriptions) return;

      if (type === 'text') {
        setEditingTextId(item);
        setEditingSpeakerLabelId(null);
      } else if (type === 'speaker') {
        setEditingSpeakerLabelId(item);
        setEditingTextId(null);
      }
    },
    [canUpdateTranscriptions]
  );

  const handleSpeakerLabelBlur = useCallback(
    (updatedSpeakerLabel: string, speakerLabelId: number) => {
      const udpatedAsrRecognitionResults = transcriptions.words.map(word =>
        word.id === speakerLabelId
          ? {
              ...word,
              speakerLabel: updatedSpeakerLabel,
            }
          : word
      );

      onUpdate(transcriptions.id, {
        asrRecognitionResults: udpatedAsrRecognitionResults,
      });

      setEditingSpeakerLabelId(null);
    },
    [onUpdate, transcriptions.id, transcriptions.words]
  );

  const handleBlur = () => {
    if (editingTextId) {
      try {
        const textIndex = slateValue.findIndex(node => node.children[0]?.id === editingTextId);

        if (textIndex !== -1) {
          const updatedText = Node.string(editor.children[textIndex]);
          const updatedTranscriptionId = editor.children[textIndex]['transcriptionId'];

          const udpatedAsrRecognitionResults = transcriptions.words.map(word =>
            word.id === editingTextId
              ? {
                  ...word,
                  text: updatedText,
                }
              : word
          );

          onUpdate(updatedTranscriptionId, {
            asrRecognitionResults: udpatedAsrRecognitionResults,
          });
        }
      } catch (error) {
        console.error('Error al obtener el texto actualizado:', error);
      }
    }
    setEditingTextId(null);
    setEditingSpeakerLabelId(null);
  };

  const decorate = useCallback(
    ([node, path]) => {
      const ranges: ExtendedDecoratedRange[] = [];

      if (typeof node !== 'object' || node === null || !('text' in node)) {
        return ranges;
      }

      const { text, startOffset, endOffset } = node;

      if (textToHighlight && typeof text === 'string') {
        let startIndex = 0;
        let index = text.indexOf(textToHighlight, startIndex);
        while (index !== -1) {
          ranges.push({
            anchor: { path, offset: index },
            focus: { path, offset: index + textToHighlight.length },
            manualHighlight: true,
          });
          startIndex = index + textToHighlight.length;
          index = text.indexOf(textToHighlight, startIndex);
        }
      }

      // Highlight searchTextToHighlight in orange
      if (searchTextToHighlight && typeof text === 'string') {
        let startIndex = 0;
        let index = text.toLowerCase().indexOf(searchTextToHighlight.toLowerCase(), startIndex);
        while (index !== -1) {
          ranges.push({
            anchor: { path, offset: index },
            focus: { path, offset: index + searchTextToHighlight.length },
            highlightOrange: true,
          });
          startIndex = index + searchTextToHighlight.length;
          index = text.toLowerCase().indexOf(searchTextToHighlight.toLowerCase(), startIndex);
        }
      }

      if (startOffset !== undefined && endOffset !== undefined) {
        if (seconds >= startOffset && seconds <= endOffset) {
          ranges.push({
            anchor: { path, offset: 0 },
            focus: { path, offset: text.length },
            highlight: true,
          });
        }
      }

      transcriptions.mentions?.forEach(mention => {
        const mentionStartSec = getSecondsFromStart(mention.start_datetime, itemStartDatetime);
        const mentionEndSec = getSecondsFromStart(mention.end_datetime, itemStartDatetime);

        if (startOffset !== undefined && endOffset !== undefined) {
          const overlaps =
            (startOffset <= mentionEndSec && endOffset >= mentionStartSec) ||
            (mentionStartSec >= startOffset && mentionEndSec <= endOffset);

          if (overlaps && mention.keywords) {
            mention.keywords.forEach(keyword => {
              let searchStart = 0;
              let index = text.toLowerCase().indexOf(keyword.toLowerCase(), searchStart);

              while (index !== -1) {
                ranges.push({
                  anchor: { path, offset: index },
                  focus: { path, offset: index + keyword.length },
                  mentionHighlight: true,
                  mentionSecond: mentionStartSec,
                });

                searchStart = index + keyword.length;
                index = text.toLowerCase().indexOf(keyword.toLowerCase(), searchStart);
              }
            });
          }
        }
      });

      return ranges;
    },
    [seconds, textToHighlight, searchTextToHighlight, transcriptions.mentions, itemStartDatetime]
  );

  const renderElement = ({ attributes, children, element }) => {
    const speakerLabel = element.children?.[0]?.speakerLabel;
    const speakerLabelId = element.children?.[0]?.id;
    const isSpeakerLabelEditable = editingSpeakerLabelId === speakerLabelId;

    return (
      <div {...attributes} className="flex gap-2 py-1">
        {speakerLabel && (
          <span
            contentEditable={isSpeakerLabelEditable}
            suppressContentEditableWarning
            onDoubleClick={() => onDoubleClickHandler(speakerLabelId, 'speaker')}
            onBlur={e => handleSpeakerLabelBlur(e.currentTarget.textContent || '', speakerLabelId)}
            className="w-10 flex-none border-r-[2px] border-[#d928f9] bg-white pr-1 text-right font-bold text-[#2e77fb]"
            style={{
              outline: isSpeakerLabelEditable ? '2px solid #2e77fb' : 'none',
            }}
          >
            {speakerLabel}
          </span>
        )}
        <div className={clsx('flex-1', underMinDesktopWith ? 'text-xs' : 'text-base')}>
          {children}
        </div>
      </div>
    );
  };

  const renderLeaf = ({ attributes, children, leaf }) => {
    const isEditable = editingTextId === leaf.id;

    const handleMentionClick = () => {
      if (leaf.startOffset !== undefined) {
        videoControlRef.current?.seekToSecond(leaf.startOffset);
      }
    };

    return (
      <span
        {...attributes}
        contentEditable={isEditable}
        suppressContentEditableWarning
        onDoubleClick={() => onDoubleClickHandler(leaf?.id, 'text')}
        onClick={handleMentionClick}
        style={{
          userSelect: 'text',
          outline: isEditable ? '2px solid #2e77fb' : 'none',
          whiteSpace: 'pre-wrap',
        }}
        className={clsx(
          leaf.highlight && 'bg-extra-80/15',
          leaf.manualHighlight && 'bg-[#2e77fb] text-white',
          leaf.mentionHighlight &&
            'cursor-pointer border-b-2 border-blue-500 bg-blue-100 font-semibold text-[#2e77fb]',
          leaf.highlightOrange && HighlightStyles.SEARCH
        )}
      >
        {children}
      </span>
    );
  };

  const onKeyDownHandler = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if ([' ', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
      event.stopPropagation();
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      editor.insertText('\n');
    }

    if (event.key === 'Shift+Enter') {
      handleBlur();
    }
  }, []);

  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      if (editorRef.current && !editorRef.current.contains(event.target as any)) {
        handleBlur();
      }
    };

    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [handleBlur]);

  return (
    <div className="h-full w-full" ref={editorRef}>
      <Slate editor={editor} initialValue={slateValue} key={transcriptions.id}>
        <Editable
          readOnly={!editingTextId}
          renderElement={renderElement}
          decorate={decorate}
          renderLeaf={renderLeaf}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onKeyDown={onKeyDownHandler}
          onDrag={() => false}
          className="focus:outline-none"
        />
      </Slate>
    </div>
  );
};
